import { ApplicationConfig, ErrorHandler, inject, LOCALE_ID, provideAppInitializer } from "@angular/core";
import { appRoutes } from "src/app/app.routing";
import { environment } from "src/environments/environment";
import { AppErrorHandler } from "src/app/providers/app-error-handler.provider";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { CachingInterceptor } from "src/app/interceptors/caching.interceptor";
import { CorrelationInterceptor } from "src/app/interceptors/correlation.interceptor";
import { KeycloakBearerInterceptor } from "src/app/interceptors/keycloak-bearer.interceptor";
import { HttpCache, HttpCacheImpl } from "src/app/services/http-cache.service";
import { provideRouter, RouteReuseStrategy, withInMemoryScrolling, withRouterConfig } from "@angular/router";
import { keycloakInitializer } from "src/app/initializers/keycloak.initializer";
import { fmsInitializer } from "src/app/initializers/fms.initializer";
import { featureEnvInitializer } from "src/app/initializers/feature-env.initializer";
import { KeycloakService } from "src/app/services/keycloak.service";
import { CustomRouteReuseStrategy } from "src/app/providers/custom-route-reuse-strategy.provider";
import { provideAnimations } from "@angular/platform-browser/animations";
import { FmsService } from "src/app/services/fms.service";
import { AfpService } from "src/app/services/prognoses-services/afp.service";
import { EngagementsService } from "src/app/services/engagements.service";
import { ApiHomeService } from "src/app/services/api-home.service";
import { CustomerService } from "src/app/services/customer.service";
import { ErrorsService } from "src/app/services/errors.service";
import { ActionIpsService } from "src/app/modules/features/pension-actions/components/action-ips/action-ips.service";
import { ExtrapensionService } from "src/app/services/extrapension.service";
import { InternalSavingsService } from "src/app/services/internal-savings.service";
import { ActionEpkService } from "src/app/modules/features/pension-actions/services/action-epk.service";
import { CurrencyPipe, DatePipe, DecimalPipe, registerLocaleData } from "@angular/common";
import localeNb from "@angular/common/locales/nb";
import { graphClientFactory } from "src/app/modules/graphql-clients/clients/graph-client";
import { HttpLink } from "apollo-angular/http";
import { EndpointService } from "src/app/services/endpoint.service";
import { provideNamedApollo } from "apollo-angular";
import { mockSavingsGraphClientFactory } from "src/app/modules/graphql-clients/clients/mock-savings-graph/mock-savings-graph-client";
import { ApiInterceptor } from "src/app/interceptors/api.interceptor";
import { OfflineDataInterceptor } from "src/app/interceptors/offline-data.interceptor";

registerLocaleData(localeNb);

const SERVICES = [
  AfpService,
  EngagementsService,
  ApiHomeService,
  CustomerService,
  ErrorsService,
  FmsService,
  ExtrapensionService,
  ActionIpsService,
  InternalSavingsService,
  KeycloakService,
  ActionEpkService,
];

const PIPES = [CurrencyPipe, DatePipe, DecimalPipe];

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ scrollPositionRestoration: "top" }),
      // Angular 22 changed the default to "always"; pin to the pre-v22 behavior.
      withRouterConfig({ paramsInheritanceStrategy: "emptyOnly" }),
    ),
    // Angular 22 defaults HttpClient to the Fetch backend (no withXhr) — intentional.
    provideHttpClient(withInterceptorsFromDi()),
    provideNamedApollo(() => {
      const httpLink = inject(HttpLink);
      const endpointService = inject(EndpointService);
      return environment.offlineMode ? mockSavingsGraphClientFactory() : graphClientFactory(httpLink, endpointService);
    }),
    SERVICES,
    { provide: LOCALE_ID, useValue: "nb-NO" },
    PIPES,
    { provide: ErrorHandler, useClass: AppErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OfflineDataInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CachingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CorrelationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    {
      provide: HttpCache,
      useClass: HttpCacheImpl,
    },
    // Must precede every other initializer — it can reload the page, and the
    // cookie it writes decides which backend the other initializers talk to.
    provideAppInitializer(() => featureEnvInitializer()),
    provideAppInitializer(() => {
      const keycloakService = inject(KeycloakService);
      return keycloakInitializer(keycloakService)();
    }),
    provideAppInitializer(() => fmsInitializer()),
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    provideAnimations(),
  ],
};
